import React, { useState, useEffect } from 'react';
import { useGoalsContext } from '../context/GoalsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import Textarea from '../components/ui/Textarea';
import OverallGoalOnboarding from '../components/onboarding/OverallGoalOnboarding';
import {
  FiTarget,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiCheck,
  FiClock,
  FiTrendingUp,
  FiUsers,
  FiActivity,
  FiFlag,
  FiX,
  FiCalendar,
  FiCheckCircle,
  FiAlertCircle
} from 'react-icons/fi';
import { getTodayDateString } from '../lib/utils';

const Goals = () => {
  const {
    overallGoals,
    sessionGoals,
    addOverallGoal,
    updateOverallGoal,
    deleteOverallGoal,
    toggleMilestone,
    addSessionGoal,
    updateSessionGoal,
    deleteSessionGoal,
    toggleSessionGoalComplete
  } = useGoalsContext();

  const [showOverallGoalForm, setShowOverallGoalForm] = useState(false);
  const [showSessionGoalForm, setShowSessionGoalForm] = useState(false);
  const [editingOverallGoal, setEditingOverallGoal] = useState(null);
  const [editingSessionGoal, setEditingSessionGoal] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const [newOverallGoal, setNewOverallGoal] = useState({
    title: '',
    description: '',
    category: 'Technical',
    targetDate: '',
    milestones: []
  });

  const [newSessionGoal, setNewSessionGoal] = useState({
    title: '',
    description: '',
    sessionDate: getTodayDateString(),
    priority: 'Medium',
    techniques: []
  });

  const [newMilestone, setNewMilestone] = useState('');
  const [newTechnique, setNewTechnique] = useState('');

  const categories = ['Technical', 'Fitness', 'Social', 'Competition', 'Mental'];
  const priorities = ['Low', 'Medium', 'High'];

  const handleCreateFirstGoalClick = () => {
    const hasCompletedOnboarding = localStorage.getItem('clinchfirst_overall_goal_onboarding_completed');
    if (!hasCompletedOnboarding && overallGoals.length === 0) {
      setShowOnboarding(true);
    } else {
      setEditingOverallGoal(null);
      setNewOverallGoal({
        title: '',
        description: '',
        category: 'Technical',
        targetDate: '',
        milestones: []
      });
      setShowOverallGoalForm(true);
      setShowSessionGoalForm(false);
    }
  };

  const handleOnboardingCreateGoal = (goalData) => {
    addOverallGoal(goalData);
  };

  const handleAddOverallGoal = () => {
    if (newOverallGoal.title.trim()) {
      addOverallGoal(newOverallGoal);
      setNewOverallGoal({
        title: '',
        description: '',
        category: 'Technical',
        targetDate: '',
        milestones: []
      });
      setShowOverallGoalForm(false);
    }
  };

  const handleUpdateOverallGoal = () => {
    if (editingOverallGoal && newOverallGoal.title.trim()) {
      updateOverallGoal(editingOverallGoal.id, {
        title: newOverallGoal.title,
        description: newOverallGoal.description,
        category: newOverallGoal.category,
        targetDate: newOverallGoal.targetDate,
        milestones: newOverallGoal.milestones
      });
      setNewOverallGoal({
        title: '',
        description: '',
        category: 'Technical',
        targetDate: '',
        milestones: []
      });
      setEditingOverallGoal(null);
      setShowOverallGoalForm(false);
    }
  };

  const handleEditOverallGoal = (goal) => {
    setEditingOverallGoal(goal);
    setNewOverallGoal({
      title: goal.title,
      description: goal.description,
      category: goal.category,
      targetDate: goal.targetDate,
      milestones: [...goal.milestones]
    });
    setShowOverallGoalForm(true);
    setShowSessionGoalForm(false);
  };

  const handleAddSessionGoal = () => {
    if (newSessionGoal.title.trim()) {
      addSessionGoal(newSessionGoal);
      setNewSessionGoal({
        title: '',
        description: '',
        sessionDate: getTodayDateString(),
        priority: 'Medium',
        techniques: []
      });
      setShowSessionGoalForm(false);
    }
  };

  const handleUpdateSessionGoal = () => {
    if (editingSessionGoal && newSessionGoal.title.trim()) {
      updateSessionGoal(editingSessionGoal.id, {
        title: newSessionGoal.title,
        description: newSessionGoal.description,
        sessionDate: newSessionGoal.sessionDate,
        priority: newSessionGoal.priority,
        techniques: newSessionGoal.techniques
      });
      setNewSessionGoal({
        title: '',
        description: '',
        sessionDate: getTodayDateString(),
        priority: 'Medium',
        techniques: []
      });
      setEditingSessionGoal(null);
      setShowSessionGoalForm(false);
    }
  };

  const handleEditSessionGoal = (goal) => {
    setEditingSessionGoal(goal);
    setNewSessionGoal({
      title: goal.title,
      description: goal.description,
      sessionDate: goal.sessionDate,
      priority: goal.priority,
      techniques: [...goal.techniques]
    });
    setShowSessionGoalForm(true);
    setShowOverallGoalForm(false);
  };

  const handleDeleteOverallGoal = (id) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      deleteOverallGoal(id);
    }
  };

  const handleDeleteSessionGoal = (id) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      deleteSessionGoal(id);
    }
  };

  const addMilestoneToNewGoal = () => {
    if (newMilestone.trim()) {
      setNewOverallGoal({
        ...newOverallGoal,
        milestones: [
          ...newOverallGoal.milestones,
          { id: Date.now(), text: newMilestone, completed: false }
        ]
      });
      setNewMilestone('');
    }
  };

  const removeMilestoneFromNewGoal = (id) => {
    setNewOverallGoal({
      ...newOverallGoal,
      milestones: newOverallGoal.milestones.filter((m) => m.id !== id)
    });
  };

  const addTechniqueToNewGoal = () => {
    if (newTechnique.trim()) {
      setNewSessionGoal({
        ...newSessionGoal,
        techniques: [...newSessionGoal.techniques, newTechnique]
      });
      setNewTechnique('');
    }
  };

  const removeTechniqueFromNewGoal = (index) => {
    setNewSessionGoal({
      ...newSessionGoal,
      techniques: newSessionGoal.techniques.filter((_, i) => i !== index)
    });
  };

  const handleCancelForm = () => {
    setShowOverallGoalForm(false);
    setShowSessionGoalForm(false);
    setEditingOverallGoal(null);
    setEditingSessionGoal(null);
    setNewOverallGoal({
      title: '',
      description: '',
      category: 'Technical',
      targetDate: '',
      milestones: []
    });
    setNewSessionGoal({
      title: '',
      description: '',
      sessionDate: getTodayDateString(),
      priority: 'Medium',
      techniques: []
    });
    setNewMilestone('');
    setNewTechnique('');
  };

  const getCategoryIcon = (category) => {
    const icons = {
      Technical: FiTarget,
      Fitness: FiActivity,
      Social: FiUsers,
      Competition: FiFlag,
      Mental: FiTrendingUp
    };
    return icons[category] || FiTarget;
  };

  const getCategoryColor = (category) => {
    const colors = {
      Technical: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
      Fitness: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
      Social: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
      Competition: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
      Mental: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      Medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      High: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    };
    return colors[priority] || 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  };

  const stats = {
    totalOverallGoals: overallGoals.length,
    activeOverallGoals: overallGoals.filter((g) => g.status === 'In Progress').length,
    avgProgress: overallGoals.length > 0
      ? Math.round(overallGoals.reduce((sum, g) => sum + g.progress, 0) / overallGoals.length)
      : 0,
    todaySessionGoals: sessionGoals.filter(
      (g) => g.sessionDate === getTodayDateString()
    ).length,
    completedSessionGoals: sessionGoals.filter((g) => g.completed).length
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Onboarding Modal */}
      <OverallGoalOnboarding
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => setShowOnboarding(false)}
        onCreateGoal={handleOnboardingCreateGoal}
      />

      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
          Goals & Objectives
        </h1>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
          Set and track your long-term goals and session-specific objectives
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Overall Goals</p>
              <FiTarget className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              {stats.totalOverallGoals}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{stats.activeOverallGoals} active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Avg Progress</p>
              <FiTrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{stats.avgProgress}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Today's Goals</p>
              <FiCalendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              {stats.todaySessionGoals}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">for this session</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Completed</p>
              <FiCheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              {stats.completedSessionGoals}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">session goals</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200 dark:from-blue-900/20 dark:to-purple-900/20 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-300 font-medium">Keep Going!</p>
              <FiFlag className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-300">💪</p>
            <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">Stay consistent</p>
          </CardContent>
        </Card>
      </div>

      {/* Overall Goals Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center">
            <FiTarget className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-500" />
            Overall Goals
          </h2>
          <Button onClick={handleCreateFirstGoalClick} >
            <FiPlus className="w-4 h-4 mr-2" />
            New Goal
          </Button>
        </div>

        {/* Add/Edit Overall Goal Form */}
        {showOverallGoalForm && (
          <Card className="mb-6 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
            <CardHeader>
              <CardTitle className="text-lg dark:text-white flex items-center justify-between">
                {editingOverallGoal ? 'Edit Overall Goal' : 'Create New Overall Goal'}
                <button onClick={handleCancelForm} className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
                  <FiX className="w-5 h-5" />
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="goal-title">Goal Title</Label>
                  <Input
                    id="goal-title"
                    placeholder="e.g., Get in Shape, Master Leg Locks"
                    value={newOverallGoal.title}
                    onChange={(e) => setNewOverallGoal({ ...newOverallGoal, title: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="goal-description">Description</Label>
                  <Textarea
                    id="goal-description"
                    placeholder="Describe your goal in detail..."
                    value={newOverallGoal.description}
                    onChange={(e) => setNewOverallGoal({ ...newOverallGoal, description: e.target.value })}
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="goal-category">Category</Label>
                    <select
                      id="goal-category"
                      value={newOverallGoal.category}
                      onChange={(e) => setNewOverallGoal({ ...newOverallGoal, category: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-1 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="goal-target-date">Target Date</Label>
                    <Input
                      id="goal-target-date"
                      type="date"
                      value={newOverallGoal.targetDate}
                      onChange={(e) => setNewOverallGoal({ ...newOverallGoal, targetDate: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Milestones</Label>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                    Add specific milestones to track progress
                  </p>
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Add a milestone..."
                      value={newMilestone}
                      onChange={(e) => setNewMilestone(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addMilestoneToNewGoal();
                        }
                      }}
                    />
                    <Button type="button" onClick={addMilestoneToNewGoal}>
                      <FiPlus className="w-4 h-4" />
                    </Button>
                  </div>
                  {newOverallGoal.milestones.length > 0 && (
                    <div className="space-y-2">
                      {newOverallGoal.milestones.map((milestone) => (
                        <div
                          key={milestone.id}
                          className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                        >
                          <span className="text-sm text-slate-900 dark:text-slate-200">{milestone.text}</span>
                          <button
                            onClick={() => removeMilestoneFromNewGoal(milestone.id)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={editingOverallGoal ? handleUpdateOverallGoal : handleAddOverallGoal}
                    className="flex-1"
                  >
                    <FiCheck className="w-4 h-4 mr-2" />
                    {editingOverallGoal ? 'Update Goal' : 'Create Goal'}
                  </Button>
                  <Button variant="outline" onClick={handleCancelForm}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overall Goals List */}
        {overallGoals.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {overallGoals.map((goal) => {
              const CategoryIcon = getCategoryIcon(goal.category);
              return (
                <Card key={goal.id} className="hover:shadow-md transition-shadow dark:border-slate-800">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CategoryIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          <CardTitle className="text-lg dark:text-white">{goal.title}</CardTitle>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{goal.description}</p>
                      </div>
                      <div className="flex gap-2 ml-2">
                        <button
                          onClick={() => handleEditOverallGoal(goal)}
                          className="text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
                        >
                          <FiEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteOverallGoal(goal.id)}
                          className="text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`px-2 py-1 text-xs rounded-full border ${getCategoryColor(
                            goal.category
                          )}`}
                        >
                          {goal.category}
                        </span>
                        {goal.targetDate && (
                          <span className="flex items-center text-xs text-slate-600 dark:text-slate-400">
                            <FiCalendar className="w-3 h-3 mr-1" />
                            Target: {new Date(goal.targetDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Progress</span>
                          <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{goal.progress}%</span>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${goal.progress}%` }}
                          />
                        </div>
                      </div>

                      {goal.milestones && goal.milestones.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Milestones ({goal.milestones.filter((m) => m.completed).length}/
                            {goal.milestones.length})
                          </p>
                          <div className="space-y-2">
                            {goal.milestones.map((milestone) => (
                              <div
                                key={milestone.id}
                                className="flex items-start space-x-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 p-2 rounded-lg transition-colors"
                                onClick={() => toggleMilestone(goal.id, milestone.id)}
                              >
                                <div
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                    milestone.completed
                                      ? 'bg-green-500 border-green-500'
                                      : 'bg-white border-slate-300 dark:bg-slate-800 dark:border-slate-600'
                                  }`}
                                >
                                  {milestone.completed && <FiCheck className="w-3 h-3 text-white" />}
                                </div>
                                <span
                                  className={`text-sm flex-1 ${
                                    milestone.completed ? 'text-slate-500 dark:text-slate-500 line-through' : 'text-slate-700 dark:text-slate-300'
                                  }`}
                                >
                                  {milestone.text}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
            <FiTarget className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No overall goals yet</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Create your first long-term goal to start tracking your progress
            </p>
            <Button onClick={handleCreateFirstGoalClick}>
              <FiPlus className="w-4 h-4 mr-2" />
              Create Your First Goal
            </Button>
          </div>
        )}
      </div>

      {/* Session Goals Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center">
            <FiClock className="w-6 h-6 mr-2 text-purple-600 dark:text-purple-500" />
            Session Goals
          </h2>
          <Button
            variant="secondary"
            onClick={() => {
              setEditingSessionGoal(null);
              setNewSessionGoal({
                title: '',
                description: '',
                sessionDate: getTodayDateString(),
                priority: 'Medium',
                techniques: []
              });
              setShowSessionGoalForm(!showSessionGoalForm);
              setShowOverallGoalForm(false);
            }}
          >
            <FiPlus className="w-4 h-4 mr-2" />
            New Session Goal
          </Button>
        </div>

        {/* Add/Edit Session Goal Form */}
        {showSessionGoalForm && (
          <Card className="mb-6 border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-900/20">
            <CardHeader>
              <CardTitle className="text-lg dark:text-white flex items-center justify-between">
                {editingSessionGoal ? 'Edit Session Goal' : 'Create New Session Goal'}
                <button onClick={handleCancelForm} className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
                  <FiX className="w-5 h-5" />
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="session-goal-title">Goal Title</Label>
                  <Input
                    id="session-goal-title"
                    placeholder="e.g., Practice Armbar Setup from Guard"
                    value={newSessionGoal.title}
                    onChange={(e) => setNewSessionGoal({ ...newSessionGoal, title: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="session-goal-description">Description</Label>
                  <Textarea
                    id="session-goal-description"
                    placeholder="What specifically do you want to work on?"
                    value={newSessionGoal.description}
                    onChange={(e) => setNewSessionGoal({ ...newSessionGoal, description: e.target.value })}
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="session-date">Session Date</Label>
                    <Input
                      id="session-date"
                      type="date"
                      value={newSessionGoal.sessionDate}
                      onChange={(e) => setNewSessionGoal({ ...newSessionGoal, sessionDate: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="session-priority">Priority</Label>
                    <select
                      id="session-priority"
                      value={newSessionGoal.priority}
                      onChange={(e) => setNewSessionGoal({ ...newSessionGoal, priority: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent mt-1 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                    >
                      {priorities.map((priority) => (
                        <option key={priority} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <Label>Related Techniques</Label>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">Tag techniques you'll focus on</p>
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Add a technique..."
                      value={newTechnique}
                      onChange={(e) => setNewTechnique(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTechniqueToNewGoal();
                        }
                      }}
                    />
                    <Button type="button" onClick={addTechniqueToNewGoal}>
                      <FiPlus className="w-4 h-4" />
                    </Button>
                  </div>
                  {newSessionGoal.techniques.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {newSessionGoal.techniques.map((tech, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-200 flex items-center gap-1"
                        >
                          {tech}
                          <button
                            onClick={() => removeTechniqueFromNewGoal(index)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <FiX className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={editingSessionGoal ? handleUpdateSessionGoal : handleAddSessionGoal}
                    className="flex-1"
                  >
                    <FiCheck className="w-4 h-4 mr-2" />
                    {editingSessionGoal ? 'Update Goal' : 'Create Goal'}
                  </Button>
                  <Button variant="outline" onClick={handleCancelForm}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Session Goals List */}
        <div className="space-y-4">
          {/* Today's Goals */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
              <FiCalendar className="w-5 h-5 mr-2 text-purple-600 dark:text-purple-400" />
              Today's Goals
            </h3>
            {sessionGoals.filter(
              (goal) => goal.sessionDate === getTodayDateString()
            ).length > 0 ? (
              <div className="space-y-3">
                {sessionGoals
                  .filter((goal) => goal.sessionDate === getTodayDateString())
                  .map((goal) => (
                    <Card
                      key={goal.id}
                      className={`hover:shadow-md transition-shadow ${
                        goal.completed ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800/50' : 'dark:border-slate-800'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => toggleSessionGoalComplete(goal.id)}
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                              goal.completed
                                ? 'bg-green-500 border-green-500'
                                : 'bg-white border-slate-300 hover:border-green-500 dark:bg-slate-800 dark:border-slate-600 dark:hover:border-green-500'
                            }`}
                          >
                            {goal.completed && <FiCheck className="w-4 h-4 text-white" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h4
                                  className={`font-semibold text-slate-900 dark:text-white ${
                                    goal.completed ? 'line-through text-slate-500 dark:text-slate-500' : ''
                                  }`}
                                >
                                  {goal.title}
                                </h4>
                                <p
                                  className={`text-sm mt-1 ${
                                    goal.completed ? 'text-slate-400 dark:text-slate-500' : 'text-slate-600 dark:text-slate-400'
                                  }`}
                                >
                                  {goal.description}
                                </p>
                              </div>
                              <div className="flex gap-2 ml-2">
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(
                                    goal.priority
                                  )}`}
                                >
                                  {goal.priority}
                                </span>
                                <button
                                  onClick={() => handleEditSessionGoal(goal)}
                                  className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
                                >
                                  <FiEdit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteSessionGoal(goal.id)}
                                  className="text-slate-400 hover:text-red-600 dark:hover:text-red-400"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            {goal.techniques && goal.techniques.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {goal.techniques.map((tech, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                  >
                                    {tech}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                <FiCalendar className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-sm text-slate-600 dark:text-slate-400">No goals set for today</p>
              </div>
            )}
          </div>

          {/* Upcoming Goals */}
          {sessionGoals.filter(
            (goal) => goal.sessionDate > getTodayDateString() && !goal.completed
          ).length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
                <FiClock className="w-5 h-5 mr-2 text-slate-600 dark:text-slate-400" />
                Upcoming Goals
              </h3>
              <div className="space-y-3">
                {sessionGoals
                  .filter(
                    (goal) => goal.sessionDate > getTodayDateString() && !goal.completed
                  )
                  .map((goal) => (
                    <Card key={goal.id} className="hover:shadow-md transition-shadow border-slate-200 dark:border-slate-800">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <FiAlertCircle className="w-5 h-5 text-slate-400 mt-1 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-slate-900 dark:text-white">{goal.title}</h4>
                                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{goal.description}</p>
                              </div>
                              <div className="flex gap-2 ml-2">
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(
                                    goal.priority
                                  )}`}
                                >
                                  {goal.priority}
                                </span>
                                <button
                                  onClick={() => handleEditSessionGoal(goal)}
                                  className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
                                >
                                  <FiEdit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteSessionGoal(goal.id)}
                                  className="text-slate-400 hover:text-red-600 dark:hover:text-red-400"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                              <span className="flex items-center">
                                <FiCalendar className="w-3 h-3 mr-1" />
                                {new Date(goal.sessionDate).toLocaleDateString()}
                              </span>
                            </div>
                            {goal.techniques && goal.techniques.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {goal.techniques.map((tech, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                  >
                                    {tech}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          )}

          {/* Completed Goals */}
          {sessionGoals.filter((g) => g.completed).length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
                <FiCheckCircle className="w-5 h-5 mr-2 text-green-600 dark:text-green-500" />
                Recently Completed
              </h3>
              <div className="space-y-3">
                {sessionGoals
                  .filter((goal) => goal.completed)
                  .slice(0, 3)
                  .map((goal) => (
                    <Card key={goal.id} className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800/50 opacity-75">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-500 mt-1 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-slate-700 dark:text-slate-300 line-through">
                                  {goal.title}
                                </h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                  Completed on {new Date(goal.sessionDate).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            {goal.notes && (
                              <div className="mt-2 p-2 bg-white dark:bg-slate-800/50 rounded border border-green-200 dark:border-green-800/50">
                                <p className="text-sm text-slate-700 dark:text-slate-300">{goal.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          )}

          {sessionGoals.length === 0 && (
            <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
              <FiClock className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No session goals yet</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Create your first session goal to focus your training
              </p>
              <Button
                onClick={() => {
                  setEditingSessionGoal(null);
                  setNewSessionGoal({
                    title: '',
                    description: '',
                    sessionDate: getTodayDateString(),
                    priority: 'Medium',
                    techniques: []
                  });
                  setShowSessionGoalForm(true);
                  setShowOverallGoalForm(false);
                }}
              >
                <FiPlus className="w-4 h-4 mr-2" />
                Create Your First Session Goal
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Goals;